import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { format } from 'date-fns';
import { ErrorMessage, getIn } from 'formik';
import InputErrorMessage from './InputErrorMessage';
import formStyles from '../../styles/forms.module.scss';
import DuetdsWrapper from './DuetdsWrapper';

function padZero(number) {
  if (number < 10) {
    return `0${number}`;
  }
  return number;
}

const datepickerLocalization = {
  de: {
    buttonLabel: 'Datum Auswählen',
    placeholder: '',
    selectedDateMessage: 'Ausgewähltes Datum ist',
    prevMonthLabel: 'Voriger Monat',
    nextMonthLabel: 'Nächster Monat',
    monthSelectLabel: 'Monat',
    yearSelectLabel: 'Jahr',
    closeLabel: 'Fenster schließen',
    calendarHeading: 'Wählen Sie ein Datum',
    dayNames: [
      'Sonntag',
      'Montag',
      'Dienstag',
      'Mittwoch',
      'Donnerstag',
      'Freitag',
      'Samstag',
    ],
    monthNames: [
      'Jänner',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ],
    monthNamesShort: [
      'Jan',
      'Feb',
      'Mär',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dez',
    ],
    locale: 'de',
  },
};

const dateAdapter = {
  // eslint-disable-next-line default-param-last
  parse(value = '', createDate) {
    const matches = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (matches) {
      return createDate(matches[3], matches[2], matches[1]);
    }
    return undefined;
  },
  format(date) {
    return `${padZero(date.getDate())}.${padZero(
      date.getMonth() + 1
    )}.${date.getFullYear()}`;
  },
};

class AccessibleDatePickerInput extends React.Component {
  constructor() {
    super();
    this.state = {
      minDate: null,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
  }

  static getDerivedStateFromProps(props) {
    const newState = {};
    const { minDate, form } = props;
    let processedMinDate = minDate;
    if (typeof minDate === 'function') {
      processedMinDate = minDate(form.values);
    }
    newState.minDate = processedMinDate;
    return newState;
  }

  handleChange(newValue) {
    const { form, field } = this.props;
    const { name } = field;
    if (newValue) {
      form.setFieldValue(name, newValue.detail.value);
    } else {
      form.setFieldValue(name, null);
    }
  }

  handleBlur() {
    const { form, field } = this.props;
    const { name } = field;
    form.setFieldTouched(name, true);
  }

  render() {
    const { labelMsg, field, required, form } = this.props;
    const { name } = field;
    const { minDate } = this.state;

    let isError = false;
    const touched = getIn(form.touched, name);
    const errors = getIn(form.errors, name);
    if (touched && errors) {
      isError = true;
    }

    return (
      <div>
        <label htmlFor={name}>
          {labelMsg && (
            <span className={isError ? formStyles.errorMessage : ''}>
              <FormattedMessage id={labelMsg} />
              {required && <span>*</span>}
            </span>
          )}
        </label>
        <div
          className={`jarvisDuetdsOverride ${
            isError ? formStyles.errorBorderDatepicker : ''
          }`}
        >
          <DuetdsWrapper
            id={name}
            value={field.value}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            dateAdapter={dateAdapter}
            localization={datepickerLocalization.de}
            min={minDate && format(minDate, 'yyyy-MM-dd')}
          />
        </div>
        <ErrorMessage name={name} component={InputErrorMessage} />
      </div>
    );
  }
}

AccessibleDatePickerInput.defaultProps = {
  labelMsg: null,
  required: false,
  minDate: undefined,
};

AccessibleDatePickerInput.propTypes = {
  /** formik form object */
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    touched: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    errors: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    values: PropTypes.object,
  }).isRequired,
  /** formik field object */
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
  }).isRequired,
  /** react-intl object */
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
    formatMessage: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired,
  }).isRequired,
  labelMsg: PropTypes.string,
  required: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.func]),
};

export default injectIntl(AccessibleDatePickerInput);
